// opd-events.js — one shared client for real-time update nudges.
//
// ONLINE-PRIMARY (main-2): the transport is Medplum WebSocket subscriptions —
// medplum-live.js translates WS notifications into {type,id,action,at}
// payloads and dispatches them as the OPD_LOCAL_EVENT CustomEvent below. The
// legacy Express SSE line (GET /api/v1/opd/events) is DELETED server-side, so
// its EventSource leg is gone from here too.
//
// Why shared: Tasks, Messaging, and the Command/Queue board all want the SAME
// behaviour — fire a callback when a relevant resource `type` changes, with
// bursts coalesced. Every callback just triggers that screen's EXISTING quiet
// refresh; the screens keep a slow poll as a FALLBACK, so if the WS line is
// unavailable nothing breaks — it just updates a little slower.

/**
 * Subscribe to OPD change events.
 *
 * @param {string[]} types  resource types this screen cares about, e.g.
 *                          ['Task'] or ['Communication']. A change whose
 *                          payload.type matches fires onChange(payload).
 * @param {(payload:{type,id,action,at})=>void} onChange
 * @returns {() => void}    unsubscribe (stops listening)
 */
// Local (same-tab) event name — medplum-live.js's WS relay dispatches these.
export const OPD_LOCAL_EVENT = 'opd:change';

export function subscribeOpdEvents(types, onChange) {
  if (typeof window === 'undefined') {
    return () => {}; // non-browser context → no-op; the poll fallback covers it.
  }
  const wanted = new Set(Array.isArray(types) ? types : [types]);
  let closed = false;
  // Trailing debounce: a burst of change frames (a bot sweep, a bulk-cohort
  // write, many staff acting at once) coalesces into AT MOST one onChange per
  // window, so the screen's refresh fires once instead of N times.
  const DEBOUNCE_MS = 350;
  let debounceTimer = null;
  let lastPayload = null;

  const deliver = (payload) => {
    if (payload && wanted.has(payload.type)) {
      lastPayload = payload;
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        const p = lastPayload; lastPayload = null;
        try { onChange(p); } catch (_) { /* a screen's refresh error must not kill the stream */ }
      }, DEBOUNCE_MS);
    }
  };

  const onLocal = (ev) => { if (!closed) deliver(ev && ev.detail); };
  window.addEventListener(OPD_LOCAL_EVENT, onLocal);

  return () => {
    closed = true;
    window.removeEventListener(OPD_LOCAL_EVENT, onLocal);
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
  };
}
